// @ts-nocheck
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { AlertCircle, Check } from "lucide-react";
import { College } from "@/hooks/useCollegeSearch";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from 'sonner';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

interface SuggestCorrectionModalProps {
  college: College | null;
  open: boolean;
  onClose: () => void;
}

const SuggestCorrectionModal: React.FC<SuggestCorrectionModalProps> = ({ college, open, onClose }) => {
  const [correction, setCorrection] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const { user, profile } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!college || !correction.trim()) return;
    
    setSubmitting(true);
    
    try {
      // Call the Supabase Edge Function to submit the correction
      const { data, error } = await invokeEdgeFunction('submit_college_correction', {
        body: {
          college_id: college.id,
          college_name: college.name,
          message: correction,
          user_id: user?.id || null,
          field_changed: 'general',
          new_value: correction
        }
      });

      if (error) {
        console.error('Error submitting correction:', error);
        toast.error('Error submitting correction');
        return;
      }

      console.log('Correction submitted successfully:', data);
      setSuccess(true);
      toast.success('Your suggestion has been submitted successfully!');
      
      // Reset form after short delay
      setTimeout(() => {
        setCorrection('');
        setSuccess(false);
        onClose();
      }, 2000);
      
    } catch (error) {
      console.error('Error in correction submission:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Suggest a Correction</DialogTitle>
          <DialogDescription>
            {college ? `Help us improve the information for ${college.name}` : 'Loading college...'}
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="flex flex-col items-center justify-center p-6 text-center">
            <div className="rounded-full bg-green-100 p-3 mb-4">
              <Check className="h-6 w-6 text-green-600" />
            </div>
            <h3 className="text-lg font-medium">Thank you for your suggestion!</h3>
            <p className="text-sm text-gray-500 mt-2">
              Our team will review your suggestion shortly!
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="bg-blue-50 border border-blue-100 rounded-md p-4 mb-4">
              <p className="text-sm text-blue-700">
                Help us improve college data! Spot an error? Submit a fix and our team will review it.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="correction">What information needs to be corrected?</Label>
              <Textarea 
                id="correction"
                value={correction}
                onChange={(e) => setCorrection(e.target.value)}
                placeholder="Please describe what information is incorrect and what it should be..."
                className="min-h-[120px]"
                required
              />
              <p className="text-xs text-gray-500">
                <AlertCircle className="inline h-3 w-3 mr-1" />
                Approved corrections will be reviewed by our team. Thank you for helping improve our data!
              </p>
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" type="button" onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting || !correction.trim()}>
                {submitting ? 'Submitting...' : 'Submit Correction'}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SuggestCorrectionModal;
